@if (isLoading()) {
  <lib-overlay-loader />
}

<div class="bg-white rounded-lg flex flex-col h-full">
  <header class="mb-4xl py-lg">
    <div class="flex items-center gap-md mb-md text-xl font-semibold">
      <a (click)="goBack()">
        <lib-chevron-down-icon
          [iconClass]="'size-10 text-neutral-900 rotate-90 p-sm rounded-lg border border-neutral-200'" />
      </a>
      {{ 'schedule_workflow_screen_title' | transloco }}
    </div>
  </header>

  <h5 class="text-neutral-900 font-semibold text-base p-lg bg-neutral-50">
    {{ 'schedule_workflow_screen_section_title' | transloco }}
  </h5>

  <form
    [formGroup]="form"
    class="grow flex flex-col py-lg"
    (ngSubmit)="saveSchedule()">
    <div class="grid grid-cols-1 md:grid-cols-3 gap-lg">
      <!-- Start Date/Time -->
      <div>
        <lib-date-picker
          id="schedule-start-date-time"
          name="start_at"
          [inputFormControlName]="'start_at'"
          [label]="'schedule_workflow_form_start_datetime_label' | transloco"
          [placeholder]="
            'schedule_workflow_form_start_datetime_label' | transloco
          "
          [required]="true"
          [showTime]="true"
          [minDate]="minStartDateTime()"
          [lang]="'current_lang' | transloco"
          [closeButtonText]="'date_picker_close_button' | transloco"
          [applyButtonText]="'date_picker_apply_button' | transloco" />
        <lib-validation-message
          [lang]="'current_lang' | transloco"
          control="start_at" />
      </div>

      <!-- Schedule Type -->
      <div>
        <lib-select
          [inputFormControlName]="'schedule_type'"
          id="schedule-type"
          name="schedule_type"
          [label]="'schedule_workflow_form_type_label' | transloco"
          [placeholder]="'schedule_workflow_form_type_label' | transloco"
          [options]="translatedScheduleTypeOptions"
          [required]="true"
          [selectedKeyValue]="'value'"
          [selectedKeyName]="'label'" />
        <lib-validation-message
          [lang]="'current_lang' | transloco"
          control="schedule_type" />
      </div>

      <!-- Repeat Every (non-one-time) -->
      @if (!isOneTime && !isAllScheduleTypesEnabled()) {
        <div>
          <lib-input
            [inputFormControlName]="'repeat_every'"
            id="schedule-repeat-every"
            type="number"
            name="repeat_every"
            [label]="'schedule_workflow_form_repeat_every_label' | transloco"
            [placeholder]="'1'"
            [required]="true"
            [min]="1"
            [endIconTemplate]="repeatEveryUnitTemplate"
            endIconClass="!size-auto" />
          <ng-template #repeatEveryUnitTemplate>
            @if (repeatEveryUnit) {
              <p class="text-xs text-neutral-500 mt-x-sm">
                {{ repeatEveryUnit }}
              </p>
            }
          </ng-template>
          <lib-validation-message
            [lang]="'current_lang' | transloco"
            control="repeat_every" />
        </div>
      }

      <!-- Recurrence fields (daily/weekly/monthly/yearly) -->
      @if (isAllScheduleTypesEnabled()) {
        <lib-schedule-recurrence-fields [form]="form" />
      }

      <!-- Days (Weekly) -->
      @if (isWeekly && !isAllScheduleTypesEnabled()) {
        <div>
          <lib-multi-select
            [inputFormControlName]="'repeat_days'"
            id="schedule-days"
            name="repeat_days"
            [label]="'schedule_workflow_form_days_label' | transloco"
            [placeholder]="
              'schedule_workflow_form_days_placeholder' | transloco
            "
            [options]="translatedWeekDayOptions"
            [required]="true"
            [selectedKeyValue]="'value'"
            [selectedKeyName]="'label'" />
          <lib-validation-message
            [lang]="'current_lang' | transloco"
            control="repeat_days" />
        </div>
      }

      <!-- Repeat On (Monthly/Yearly) -->
      @if ((isMonthly || isYearly) && !isAllScheduleTypesEnabled()) {
        <div>
          <lib-select
            [inputFormControlName]="'repeat_on_by_day_of_month'"
            id="schedule-repeat-on"
            name="repeat_on_by_day_of_month"
            [label]="'schedule_workflow_form_repeat_on_label' | transloco"
            [placeholder]="
              'schedule_workflow_form_repeat_on_placeholder' | transloco
            "
            [options]="repeatOnOptions()"
            [required]="true"
            [selectedKeyValue]="'value'"
            [selectedKeyName]="'label'" />
          <lib-validation-message
            [lang]="'current_lang' | transloco"
            control="repeat_on_by_day_of_month" />
        </div>
      }

      <!-- End Date/Time (non-one-time) -->
      @if (!isOneTime) {
        <div>
          <lib-date-picker
            id="schedule-end-date-time"
            name="end_at"
            [inputFormControlName]="'end_at'"
            [label]="'schedule_workflow_form_end_datetime_label' | transloco"
            [placeholder]="
              'schedule_workflow_form_end_datetime_label' | transloco
            "
            [required]="true"
            [showTime]="true"
            [minDate]="minEndDateTime()"
            [lang]="'current_lang' | transloco"
            [closeButtonText]="'date_picker_close_button' | transloco"
            [applyButtonText]="'date_picker_apply_button' | transloco" />
          <lib-validation-message
            [lang]="'current_lang' | transloco"
            control="end_at" />
        </div>
      }
    </div>

    <!-- Description -->
    <div class="mt-lg md:max-w-[42rem]">
      <lib-rich-editor-dual-lang-text-area
        [isSingleInput]="true"
        id="schedule-description"
        [inputFormControlName]="'description'"
        name="description"
        [label]="'schedule_workflow_form_description_label' | transloco"
        [maxlength]="3000" />
      <lib-validation-message
        [lang]="'current_lang' | transloco"
        control="description" />
    </div>

    <!-- Footer -->
    <div
      class="mt-auto pt-xl border-t border-neutral-200 flex items-center justify-end gap-md">
      <lib-button
        id="schedule-cancel-btn"
        [label]="'cancel_schedule_form_title' | transloco"
        [btnClass]="'px-2xl py-sm rounded-md text-neutral-700 bg-white text-lg font-medium border border-neutral-300'"
        (click)="goBack()"
        (keydown.enter)="goBack()" />
      <lib-button
        id="schedule-submit-btn"
        [type]="ButtonType.SUBMIT"
        [label]="'schedule_workflow_form_save_button_label' | transloco"
        [btnClass]="'px-2xl py-sm rounded-md text-lg font-medium border-none bg-primary-500 text-white hover:bg-primary-600 disabled:bg-primary-200'"
        [disabled]="form.invalid || isSubmitting()" />
    </div>
  </form>
</div>
